import React from 'react';
import { BadgeCheck, GraduationCap, Clock, Shield, Heart } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';

const EligibilitySection = () => {
  const {
    profile,
    expandedSections,
    saving,
    updateEligibility,
    saveProfile,
    toggleSection,
    isSectionDirty,
  } = useProfile();

  const isExpanded = expandedSections.includes('eligibility');
  const isDirty = isSectionDirty('eligibility');

  const eligibilityOptions = [
    {
      key: 'isStudent',
      label: 'Student',
      description: 'Active student with .edu email',
      icon: GraduationCap,
      discounts: [
        { name: 'Spotify Premium + Hulu', detail: '$5.99/mo (70% off)', url: 'https://www.spotify.com/student/' },
        { name: 'Amazon Prime Student', detail: '50% off + 6-mo free trial', url: 'https://www.amazon.com/amazonprime?primeCampaignId=studentWlpPr498' },
        { name: 'Apple Music Student', detail: '$5.99/mo + Apple TV+ free', url: 'https://support.apple.com/en-us/108657' },
        { name: 'YouTube Premium Student', detail: '$7.99/mo', url: 'https://www.youtube.com/premium/student' },
        { name: 'Peacock Student', detail: '$1.99/mo', url: 'https://www.peacocktv.com/student' },
      ],
    },
    {
      key: 'isSenior',
      label: 'Senior (55+)',
      description: 'Age 55 or older',
      icon: Clock,
      discounts: [
        { name: 'T-Mobile 55+ Plans', detail: 'Includes Netflix', url: 'https://www.t-mobile.com/cell-phone-plans/senior-discounts' },
        { name: 'Paramount+ w/ Walmart+', detail: 'Walmart+ $49/yr for seniors', url: 'https://www.walmart.com/plus' },
        { name: 'Verizon 55+ Plans', detail: 'Includes Disney+/Hulu/ESPN+', url: 'https://www.verizon.com/plans/55-plus/' },
      ],
    },
    {
      key: 'isVeteran',
      label: 'Veteran or Active Military',
      description: 'Current or former military service',
      icon: Shield,
      discounts: [
        { name: 'Veterans Advantage', detail: 'Streaming discounts hub', url: 'https://www.veteransadvantage.com/' },
        { name: 'T-Mobile Military Plans', detail: '50% off + Netflix', url: 'https://www.t-mobile.com/cell-phone-plans/military-discount' },
        { name: 'Verizon Military', detail: 'Discounted plans + streaming', url: 'https://www.verizon.com/military/' },
        { name: 'Apple Military', detail: '10% off hardware', url: 'https://www.apple.com/shop/browse/home/veterans_background' },
      ],
    },
    {
      key: 'isFirstResponder',
      label: 'First Responder',
      description: 'Police, Fire, EMT, or Healthcare worker',
      icon: Heart,
      discounts: [
        { name: 'T-Mobile First Responder', detail: '50% off plans + Netflix', url: 'https://www.t-mobile.com/cell-phone-plans/first-responder-background' },
        { name: 'Verizon First Responder', detail: 'Discounted plans + streaming', url: 'https://www.verizon.com/featured/first-responders/' },
        { name: 'ID.me Discounts', detail: 'Verify for various offers', url: 'https://www.id.me/' },
        { name: 'Apple First Responder', detail: '10% off hardware', url: 'https://www.apple.com/shop/browse/home/veterans_background' },
      ],
    },
  ];

  const activeCount = Object.values(profile.eligibility).filter(Boolean).length;

  const summary = activeCount > 0
    ? `${activeCount} discount${activeCount > 1 ? 's' : ''} active`
    : 'No discounts selected';

  return (
    <SectionWrapper
      title="Eligibility & Discounts"
      icon={BadgeCheck}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('eligibility')}
      isDirty={isDirty}
      isSaving={saving}
      onSave={saveProfile}
      summary={summary}
      badge={activeCount > 0 ? activeCount : null}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Select all that apply to unlock special streaming discounts:
        </p>

        <div className="space-y-3">
          {eligibilityOptions.map(option => {
            const isChecked = profile.eligibility[option.key];
            const Icon = option.icon;

            return (
              <label
                key={option.key}
                className={`flex items-start p-4 rounded-lg cursor-pointer transition-all border-2 ${
                  isChecked
                    ? 'bg-purple-50 border-purple-300'
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => updateEligibility({ [option.key]: e.target.checked })}
                  className="w-5 h-5 mt-0.5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${isChecked ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className={`font-medium ${isChecked ? 'text-purple-900' : 'text-gray-900'}`}>
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                  
                  {/* Show available discounts when selected */}
                  {isChecked && option.discounts.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-purple-200">
                      <p className="text-xs font-medium text-purple-700 mb-1">Available discounts:</p>
                      <ul className="text-xs space-y-1">
                        {option.discounts.map((discount, idx) => (
                          <li key={idx} className="flex items-center justify-between">
                            <span className="text-purple-600">
                              • {discount.name} — {discount.detail}
                            </span>
                            <a
                              href={discount.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-purple-700 hover:text-purple-900 underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Visit →
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {activeCount === 0 && (
          <p className="text-sm text-gray-500 text-center mt-4">
            💡 Many streaming services offer significant discounts for these groups!
          </p>
        )}
      </div>
    </SectionWrapper>
  );
};

export default EligibilitySection;